import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {useDateQueryParameter, useStringListQueryState, useStringQueryState} from "../../utils/useQueryState.js";
import moment from "moment-timezone";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import {ProjectApi} from "../../api/authority/generated/index";

export const FilterContext = createContext(null);

const CUSTOMER_WHOLESALE_FILTERS = new Set(['wholesale', 'non_wholesale']);

export function normalizeCustomerWholesaleSelection(value) {
    return CUSTOMER_WHOLESALE_FILTERS.has(value) ? value : null;
}

export function getCustomerWholesaleRequestFilter(customerWholesale) {
    const normalized = normalizeCustomerWholesaleSelection(customerWholesale);
    return normalized === null ? {} : {customerWholesale: normalized};
}

export function resolveCustomerWholesaleSelection(selection, projectId, isAvailable) {
    if (!isAvailable || selection?.projectId !== projectId) {
        return null;
    }
    return normalizeCustomerWholesaleSelection(selection?.value);
}

export default function DashboardFilterContextProvider({children}) {

    let {context, getAuthorityConfiguration} = useContext(GlobalContext);

    const currentProject = context?.currentUserProjects?.find((project) => project.id === context.currentProjectId) ?? null;
    const customerClassificationAvailable = currentProject?.shoptetCustomerClassificationAvailable === true;
    const [customerWholesaleSelection, setCustomerWholesaleSelection] = useState({projectId: null, value: null});
    const customerWholesale = resolveCustomerWholesaleSelection(
        customerWholesaleSelection,
        context?.currentProjectId,
        customerClassificationAvailable,
    );
    const customerWholesaleRequestFilter = useMemo(
        () => getCustomerWholesaleRequestFilter(customerWholesale),
        [customerWholesale],
    );

    useEffect(() => {
        setCustomerWholesaleSelection({projectId: context?.currentProjectId ?? null, value: null});
    }, [context?.currentProjectId, customerClassificationAvailable]);

    const setCustomerWholesale = (value) => {
        const normalized = normalizeCustomerWholesaleSelection(value);
        if (!customerClassificationAvailable || normalized === null) {
            setCustomerWholesaleSelection({projectId: context?.currentProjectId ?? null, value: null});
            return;
        }
        setCustomerWholesaleSelection({projectId: context.currentProjectId, value: normalized});
    };

    let authorityConfiguration = useAuthorityConfiguration();

    // DateRange:
    let [since, setSince] = useDateQueryParameter('since', moment().startOf('month'));
    let [until, setUntil] = useDateQueryParameter('until', moment());

    // -----------------------------------------------------------------------------------------------------------------
    // -- -- Sources
    // -----------------------------------------------------------------------------------------------------------------
    let [sourcesPossible, setSourcesPossible] = useState([]);
    let [sourcesRemoved, setSourcesRemoved] = useStringListQueryState('sourcesRemoved', []);

    let sourceRegister = (source) => {
        setSourcesPossible((sourcesPossible) => {
            if (sourcesPossible.includes(source)) {
                return sourcesPossible;
            }

            return [...sourcesPossible, source];
        });
    };

    // -----------------------------------------------------------------------------------------------------------------
    // -- -- Campaigns
    // -----------------------------------------------------------------------------------------------------------------

    let [campaignSearchFilter, setCampaignSearchFilter] = useStringQueryState('campaignSearchFilter', '');
    let [campaignsAllowListed, setCampaignsAllowListed] = useStringListQueryState('campaignsAllowListed', []);

    let [campaignsPossible, setCampaignsPossible] = useState({});

    let campaignRegister = (campaign) => {
        setCampaignsPossible((campaignsPossible) => {

            let campaignKeys = Object.keys(campaignsPossible);

            if (campaignKeys.includes(campaign.remoteId)) {
                return campaignsPossible;
            }

            const campaigns = {...campaignsPossible};
            campaigns[campaign.remoteId] = campaign;
            return campaigns;
        });
    };
    // -----------------------------------------------------------------------------------------------------------------
    // -- -- Centres & Domains
    // -----------------------------------------------------------------------------------------------------------------

    let [centresAllowListed, setCentresAllowListed] = useStringListQueryState('centresAllowListed', []);
    let [centresPossible, setCentresPossible] = useState([]);

    let [domainsAllowListed, setDomainsAllowListed] = useStringListQueryState('domainsAllowListed', []);
    let [domainsPossible, setDomainsPossible] = useState([]);

    const projectCentresCount = centresPossible.length;
    const projectDomainsCount = domainsPossible.length;

    // -----------------------------------------------------------------------------------------------------------------
    // -- -- Centres
    // -----------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        if (!context?.currentProjectId) {
            setCentresPossible([]);
            return;
        }

        let active = true;
        setCentresPossible([]);

        new ProjectApi(authorityConfiguration)
            .listProjectCentres({
                projectId: context.currentProjectId
            })
            .then((centres) => {
                if (active) setCentresPossible(centres);
            })
            .catch(() => {
                if (active) setCentresPossible([]);
            });

        return () => {
            active = false;
        };
    }, [context.currentProjectId]);

    // -----------------------------------------------------------------------------------------------------------------
    // -- -- Domains
    // -----------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        if (!context?.currentProjectId) {
            setDomainsPossible([]);
            return;
        }

        let active = true;
        setDomainsPossible([]);

        new ProjectApi(authorityConfiguration)
            .listProjectDomains({
                projectId: context.currentProjectId
            })
            .then((domains) => {
                if (active) setDomainsPossible(domains);
            })
            .catch(() => {
                if (active) setDomainsPossible([]);
            });

        return () => {
            active = false;
        };
    }, [context.currentProjectId]);

    useEffect(() => {
        setSourcesPossible([]);
        setCampaignsPossible([]);
    }, [domainsAllowListed, centresAllowListed]);

    return (
        <FilterContext.Provider value={{
            // Date
            since: since, setSince: setSince,
            until: until, setUntil: setUntil,

            // Current customer group classification (Shoptet only)
            customerClassificationAvailable,
            customerWholesale,
            setCustomerWholesale,
            customerWholesaleRequestFilter,

            // Sources
            sourcesPossible: sourcesPossible, setSourcesPossible: setSourcesPossible,
            sourcesRemoved: sourcesRemoved, setSourcesRemoved: setSourcesRemoved,
            sourceRegister: sourceRegister,

            // Campaigns
            campaignSearchFilter: campaignSearchFilter, setCampaignSearchFilter: setCampaignSearchFilter,
            campaignsAllowListed: campaignsAllowListed, setCampaignsAllowListed: setCampaignsAllowListed,
            campaignsPossible: campaignsPossible, setCampaignsPossible: setCampaignsPossible,
            campaignRegister: campaignRegister,

            // Centres
            centresPossible: centresPossible,
            projectCentresCount: projectCentresCount,
            centresAllowListed: centresAllowListed, setCentresAllowListed: setCentresAllowListed,

            // Domains
            domainsPossible: domainsPossible,
            projectDomainsCount: projectDomainsCount,
            domainsAllowListed: domainsAllowListed, setDomainsAllowListed: setDomainsAllowListed,
        }}>
            {children}
        </FilterContext.Provider>
    );
}
